
import React, { useState, useEffect, useRef } from 'react';
import { Send, Bot, Loader2, Sparkles, Terminal, ShieldAlert, HardHat, Download, Crown, Database, Code2 } from 'lucide-react';
import { ProjectState, ChatMessage, Snapshot, AgentRole, AppSettings } from '../types';
import { queryMultiplexer } from '../services/aiService';
import SnapshotExplorer from './SnapshotExplorer';

interface AIAssistantProps {
  projectState: ProjectState;
  snapshots: Snapshot[];
  settings: AppSettings;
  onUpdateProject: (newState: ProjectState, reasoning: string, intent: any, tag: string, agent: AgentRole) => void;
  onRestoreSnapshot: (snap: Snapshot) => void;
}

const AIAssistant: React.FC<AIAssistantProps> = ({ 
  projectState, 
  snapshots, 
  settings,
  onUpdateProject,
  onRestoreSnapshot 
}) => {
  const [input, setInput] = useState('');
  const [isThinking, setIsThinking] = useState(false);
  const [activeTab, setActiveTab] = useState<'chat' | 'memory'>('chat');
  const [selectedAgent, setSelectedAgent] = useState<AgentRole>(AgentRole.ARCHITECT);

  const [chatHistory, setChatHistory] = useState<ChatMessage[]>([]);
  const bottomRef = useRef<HTMLDivElement>(null);

  // Load History from LocalStorage
  useEffect(() => {
    const savedChat = localStorage.getItem('devpath_chat_history');
    if (savedChat) {
      try {
        setChatHistory(JSON.parse(savedChat));
      } catch (e) {
        console.error("Failed to parse chat history", e);
      }
    } else {
      // Default Start Message
      setChatHistory([
        {
          role: 'model',
          agentRole: AgentRole.ARCHITECT,
          content: "System Online. I am the Lead Architect. I can structure your project plan. Select the 'Risk Analyst' if you need a critique.",
          timestamp: Date.now()
        }
      ]);
    }
  }, []);

  // Save History to LocalStorage
  useEffect(() => {
    if (chatHistory.length > 0) {
      localStorage.setItem('devpath_chat_history', JSON.stringify(chatHistory));
    }
  }, [chatHistory]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatHistory, activeTab]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isThinking) return;

    const userMsg: ChatMessage = { role: 'user', content: input, timestamp: Date.now() };
    setChatHistory(prev => [...prev, userMsg]);
    setInput('');
    setIsThinking(true);

    try {
      // 1. Call the Multiplexer with the selected Agent AND Settings
      const response = await queryMultiplexer(projectState, userMsg.content, selectedAgent, settings);

      // 2. Add AI response to chat
      const aiMsg: ChatMessage = { 
        role: 'model', 
        agentRole: selectedAgent,
        content: `**Strategy:** ${response.intent}\n\n${response.reasoning}`, 
        timestamp: Date.now() 
      };
      setChatHistory(prev => [...prev, aiMsg]);

      // 3. Propagate changes
      const newProjectState: ProjectState = {
        ...projectState,
        tasks: response.tasks
      };
      
      onUpdateProject(newProjectState, response.reasoning, response.intent, response.tagName, selectedAgent);

    } catch (error: any) {
      setChatHistory(prev => [...prev, { 
        role: 'model', 
        content: `Error: ${error.message || "Failed to update project."}`, 
        timestamp: Date.now() 
      }]);
    } finally {
      setIsThinking(false);
    }
  };

  const handleDownloadChat = () => {
    const text = chatHistory.map(m => {
        const speaker = m.role === 'user' ? 'USER' : (m.agentRole || 'AI').toUpperCase();
        const date = new Date(m.timestamp).toLocaleString();
        return `[${date}] ${speaker}:\n${m.content}\n-------------------`;
    }).join('\n\n');

    const dataStr = "data:text/markdown;charset=utf-8," + encodeURIComponent(text);
    const downloadAnchorNode = document.createElement('a');
    downloadAnchorNode.setAttribute("href", dataStr);
    downloadAnchorNode.setAttribute("download", `devpath_chat_notes_${new Date().toISOString().slice(0,10)}.md`);
    document.body.appendChild(downloadAnchorNode);
    downloadAnchorNode.click();
    downloadAnchorNode.remove();
  };

  const getAgentColor = (role?: AgentRole) => {
      switch(role) {
          case AgentRole.ARCHITECT: return 'text-indigo-400 bg-indigo-500/20';
          case AgentRole.SW_ARCHITECT: return 'text-blue-400 bg-blue-500/20';
          case AgentRole.RISK_ANALYST: return 'text-orange-400 bg-orange-500/20';
          case AgentRole.STRATEGIST: return 'text-purple-400 bg-purple-500/20';
          case AgentRole.DB_ARCHITECT: return 'text-emerald-400 bg-emerald-500/20';
          default: return 'text-slate-400 bg-slate-500/20';
      }
  };

  const getAgentIcon = (role?: AgentRole) => {
      switch(role) {
          case AgentRole.ARCHITECT: return <HardHat className="w-4 h-4" />;
          case AgentRole.SW_ARCHITECT: return <Code2 className="w-4 h-4" />;
          case AgentRole.RISK_ANALYST: return <ShieldAlert className="w-4 h-4" />;
          case AgentRole.STRATEGIST: return <Crown className="w-4 h-4" />;
          case AgentRole.DB_ARCHITECT: return <Database className="w-4 h-4" />;
          default: return <Bot className="w-4 h-4" />;
      }
  };

  return (
    <div className="flex flex-col h-full bg-slate-900 border-l border-slate-800 w-96 shadow-2xl">
      {/* Header */}
      <div className="p-4 border-b border-slate-800 bg-slate-900/50 backdrop-blur">
        <div className="flex items-center gap-3 mb-4">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${
                selectedAgent === AgentRole.ARCHITECT ? 'bg-gradient-to-br from-indigo-500 to-purple-600' :
                selectedAgent === AgentRole.SW_ARCHITECT ? 'bg-gradient-to-br from-blue-500 to-indigo-600' :
                selectedAgent === AgentRole.RISK_ANALYST ? 'bg-gradient-to-br from-orange-500 to-red-600' :
                selectedAgent === AgentRole.STRATEGIST ? 'bg-gradient-to-br from-purple-600 to-pink-600' :
                'bg-gradient-to-br from-emerald-500 to-teal-600'
            }`}>
                {getAgentIcon(selectedAgent)}
            </div>
            <div>
            <h2 className="font-semibold text-white">Project Multiplexer</h2>
            <p className="text-xs text-slate-400">Current Agent: {selectedAgent}</p>
            </div>
        </div>

        {/* Tabs & Actions */}
        <div className="flex items-center gap-2 mb-3">
            <div className="flex flex-1 bg-slate-800 rounded-lg p-1">
            <button
                onClick={() => setActiveTab('chat')}
                className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-all ${
                activeTab === 'chat' ? 'bg-indigo-600 text-white shadow' : 'text-slate-400 hover:text-slate-200'
                }`}
            >
                Live Chat
            </button>
            <button
                onClick={() => setActiveTab('memory')}
                className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-all ${
                activeTab === 'memory' ? 'bg-indigo-600 text-white shadow' : 'text-slate-400 hover:text-slate-200'
                }`}
            >
                Memory
            </button>
            </div>
            <button 
                onClick={handleDownloadChat}
                className="p-2 bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white rounded-lg transition-colors"
                title="Download Chat Notes"
            >
                <Download className="w-4 h-4" />
            </button>
        </div>

        {/* Agent Selector (The Multiplexer Control) */}
        {activeTab === 'chat' && (
            <div className="flex gap-2 p-1 bg-slate-950/50 rounded-lg border border-slate-800 overflow-x-auto custom-scrollbar">
                <button 
                    onClick={() => setSelectedAgent(AgentRole.ARCHITECT)}
                    className={`flex-1 min-w-[60px] text-[9px] py-1.5 rounded flex items-center justify-center gap-1 ${
                        selectedAgent === AgentRole.ARCHITECT ? 'bg-indigo-500/20 text-indigo-300 border border-indigo-500/50' : 'text-slate-500 hover:text-slate-300'
                    }`}
                >
                    <HardHat className="w-3 h-3" /> Arch
                </button>
                 <button 
                    onClick={() => setSelectedAgent(AgentRole.SW_ARCHITECT)}
                    className={`flex-1 min-w-[60px] text-[9px] py-1.5 rounded flex items-center justify-center gap-1 ${
                        selectedAgent === AgentRole.SW_ARCHITECT ? 'bg-blue-500/20 text-blue-300 border border-blue-500/50' : 'text-slate-500 hover:text-slate-300'
                    }`}
                    title="Software Architect (High Logic)"
                >
                    <Code2 className="w-3 h-3" /> SW.Arch
                </button>
                <button 
                     onClick={() => setSelectedAgent(AgentRole.RISK_ANALYST)}
                     className={`flex-1 min-w-[60px] text-[9px] py-1.5 rounded flex items-center justify-center gap-1 ${
                        selectedAgent === AgentRole.RISK_ANALYST ? 'bg-orange-500/20 text-orange-300 border border-orange-500/50' : 'text-slate-500 hover:text-slate-300'
                    }`}
                >
                    <ShieldAlert className="w-3 h-3" /> Risk
                </button>
                <button 
                     onClick={() => setSelectedAgent(AgentRole.STRATEGIST)}
                     className={`flex-1 min-w-[60px] text-[9px] py-1.5 rounded flex items-center justify-center gap-1 ${
                        selectedAgent === AgentRole.STRATEGIST ? 'bg-purple-500/20 text-purple-300 border border-purple-500/50' : 'text-slate-500 hover:text-slate-300'
                    }`}
                >
                    <Crown className="w-3 h-3" /> Strat
                </button>
                 <button 
                     onClick={() => setSelectedAgent(AgentRole.DB_ARCHITECT)}
                     className={`flex-1 min-w-[60px] text-[9px] py-1.5 rounded flex items-center justify-center gap-1 ${
                        selectedAgent === AgentRole.DB_ARCHITECT ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/50' : 'text-slate-500 hover:text-slate-300'
                    }`}
                >
                    <Database className="w-3 h-3" /> DB
                </button>
            </div>
        )}
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-y-auto p-4 custom-scrollbar">
        {activeTab === 'chat' ? (
          <div className="space-y-4">
            {chatHistory.map((msg, idx) => (
              <div key={idx} className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                
                {/* Avatar */}
                <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                  msg.role === 'user' ? 'bg-slate-700' : getAgentColor(msg.agentRole).split(' ')[1] // Extract bg class
                }`}>
                  {msg.role === 'user' ? <Terminal className="w-4 h-4" /> : getAgentIcon(msg.agentRole)}
                </div>

                {/* Message Bubble */}
                <div className={`rounded-2xl p-3 max-w-[85%] text-sm ${
                  msg.role === 'user' 
                    ? 'bg-slate-700 text-white rounded-tr-sm' 
                    : 'bg-slate-800 text-slate-200 rounded-tl-sm border border-slate-700'
                }`}>
                  {msg.agentRole && (
                      <span className={`text-[10px] font-bold uppercase tracking-wider block mb-1 ${
                          getAgentColor(msg.agentRole).split(' ')[0] // Extract text class
                      }`}>
                          {msg.agentRole}
                      </span>
                  )}
                  <p className="whitespace-pre-wrap leading-relaxed">{msg.content}</p>
                </div>
              </div>
            ))}
            {isThinking && (
              <div className="flex gap-3">
                 <div className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center shrink-0">
                    <Loader2 className="w-4 h-4 text-slate-400 animate-spin" />
                 </div>
                 <div className="text-xs text-slate-400 flex items-center h-8">
                   {selectedAgent === AgentRole.ARCHITECT ? 'Architect is structuring...' : 
                    selectedAgent === AgentRole.SW_ARCHITECT ? 'SW Architect is analyzing stack...' :
                    selectedAgent === AgentRole.STRATEGIST ? 'Strategist is thinking...' : 
                    selectedAgent === AgentRole.DB_ARCHITECT ? 'DB Architect is planning schema...' : 'Analyst is simulating risks...'}
                 </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>
        ) : (
          <SnapshotExplorer snapshots={snapshots} onRestore={onRestoreSnapshot} />
        )}
      </div>

      {/* Input Area */}
      {activeTab === 'chat' && (
        <div className="p-4 border-t border-slate-800 bg-slate-900">
          <form onSubmit={handleSubmit} className="relative">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={
                selectedAgent === AgentRole.ARCHITECT ? "Instruct the Architect..." : 
                selectedAgent === AgentRole.SW_ARCHITECT ? "Ask for Technical detail..." :
                selectedAgent === AgentRole.STRATEGIST ? "Ask for Strategic direction..." :
                selectedAgent === AgentRole.DB_ARCHITECT ? "Define Schema or Tables..." :
                "Ask Analyst to find risks..."
              }
              className="w-full bg-slate-800 text-white placeholder-slate-500 rounded-xl pl-4 pr-12 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/50 border border-slate-700"
              disabled={isThinking}
            />
            <button
              type="submit"
              disabled={!input.trim() || isThinking}
              className={`absolute right-2 top-2 p-1.5 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
                  selectedAgent === AgentRole.ARCHITECT ? 'bg-indigo-600 hover:bg-indigo-500' : 
                  selectedAgent === AgentRole.SW_ARCHITECT ? 'bg-blue-600 hover:bg-blue-500' : 
                  selectedAgent === AgentRole.STRATEGIST ? 'bg-purple-600 hover:bg-purple-500' :
                  selectedAgent === AgentRole.DB_ARCHITECT ? 'bg-emerald-600 hover:bg-emerald-500' :
                  'bg-orange-600 hover:bg-orange-500'
              }`}
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default AIAssistant;
